
/**
 * Pet Parent Detail Component
 * - Displays pet parent profile information
 * - Shows contact details, join date, and consultation history
 * - Lists all pets owned by the parent with breed and age
 * - Includes account status and activity information
 */

import React, { useState } from "react";
import {
  FaRegCalendarAlt,
  FaRegEnvelope,
  FaRegFileAlt,
  FaPaw,
} from "react-icons/fa";
import { MdPhoneInTalk } from "react-icons/md";
import AvatarBlock from "./Shared/AvatarBlock";
import InfoRow from "./Shared/InfoRow";
import StatusBadge from "./Shared/StatusBadge";
import SectionHeader from "./Shared/SectionHeader";
import ImageModal from "../common/ImageModal";
import { formatDate, calculateAge } from "./Shared/helpers";

const PetParentDetail = ({ data }) => {
  const [selectedImage, setSelectedImage] = useState(null);

  console.log('PetParentDetail - Data received:', data);
  console.log('PetParentDetail - Active status:', data?.active, typeof data?.active);

  const image = data.avatar || "https://placehold.co/80x80";
  const name = data.name || "NA";
  const email = data.email || "NA";
  const joined = formatDate(data.created_at);
  const lastVisit = formatDate(data.updated_at);
  const consultations = data.appointments_counts
    ? `${data.appointments_counts} Consultations`
    : "NA";
  const mobile = data.mobile || "NA";
  const status = data.active ? "Active" : "Inactive";

  console.log('PetParentDetail - Status badge value:', status);

  const pets =
    data.pets?.map((p) => ({
      name: p.name,
      breed: p.breed,
      age: calculateAge(p.date_of_birth),
    })) || [];

  // All info rows together
  const infoItems = [
    { icon: FaRegEnvelope, label: email },
    { icon: FaRegCalendarAlt, label: `Joined: ${joined}` },
    { icon: FaRegFileAlt, label: consultations },
    { icon: FaRegCalendarAlt, label: `Last Visit: ${lastVisit}` },
    { icon: MdPhoneInTalk, label: mobile },
  ];

  // Split into two columns dynamically (left/right)
  const mid = Math.ceil(infoItems.length / 2);
  const leftInfo = infoItems.slice(0, mid);
  const rightInfo = infoItems.slice(mid);

  return (
    <div className="flex flex-col gap-8 mt-6 lg:mt-10">
      {/* Header with Avatar + Status */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-items-start gap-4 lg:gap-40">
        <AvatarBlock image={image} name={name} onImageClick={setSelectedImage} />
        <StatusBadge status={status} />
      </div>

      {/* Info Section in Two Columns */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 lg:gap-8 text-gray-800 text-sm sm:text-base mt-2 pl-4 lg:ml-20">
        {/* Left Column */}
        <div className="flex flex-col gap-3">
          {leftInfo.map((item, idx) => (
            <InfoRow key={idx} icon={item.icon}>
              {item.label}
            </InfoRow>
          ))}
        </div>

        {/* Right Column */}
        <div className="flex flex-col gap-3">
          {rightInfo.map((item, idx) => (
            <InfoRow key={idx} icon={item.icon}>
              {item.label}
            </InfoRow>
          ))}
        </div>
      </div>

      {/* Pets Section */}
      <div>
        <SectionHeader className="mt-2 lg:mt-6">Pets</SectionHeader>
        <div className="flex flex-col gap-4">
          {pets.length > 0 ? (
            pets.map((pet, i) => (
              <div
                key={i}
                className="rounded-xl bg-blue-50 flex items-start gap-3 px-4 py-3"
              >
                <FaPaw className="text-blue-700 mt-1" />
                <div>
                  <div className="font-semibold text-gray-900">{pet.name}</div>
                  <div className="text-gray-600 text-sm">
                    {pet.breed} • {pet.age}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-gray-500 text-sm italic">No pets found</div>
          )}
        </div>
      </div>

      {/* Image Modal */}
      <ImageModal
        open={!!selectedImage}
        onClose={() => setSelectedImage(null)}
        imageSrc={selectedImage}
        altText={`${name} avatar`}
      />
    </div>
  );
};

export default PetParentDetail;
